import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, Crown } from "lucide-react";
import { startCheckout } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const PRO_FEATURES = [
  "Unlimited shows every month",
  "No watermark on your clips",
  "Full 1080p vertical exports",
  "Bulk & per-guest reel downloads",
  "Everything in the roster toolkit",
];

export function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => { setReason(e.detail || ""); setOpen(true); };
    window.addEventListener("open-upgrade", handler);
    return () => window.removeEventListener("open-upgrade", handler);
  }, []);

  const upgrade = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { checkout_url } = await startCheckout(window.location.origin, lookup_key);
      window.location.href = checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          data-testid="upgrade-modal"
        >
          <motion.div
            className="relative w-full max-w-md border border-accent/40 bg-card p-8"
            initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setOpen(false)} data-testid="upgrade-close"
                    className="absolute right-4 top-4 text-muted-foreground hover:text-white">
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-accent">
              <Crown className="h-5 w-5" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Go unlimited</span>
            </div>
            {reason && <p className="mt-3 font-body text-sm text-primary">{reason}</p>}
            <div className="mt-5 space-y-3">
              {[
                { key: "creator_monthly", name: "Creator", price: 29, blurb: "Unlimited shows, Style DNA, Expand, Top Performers, no watermark, 1080p" },
                { key: "pro_monthly", name: "Pro", price: 79, blurb: "Everything in Creator + priority processing & bulk exports", best: true },
              ].map((t) => (
                <div key={t.key} className={`border p-4 ${t.best ? "border-accent bg-accent/[0.06]" : "border-border"}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-xl tracking-wide text-white">{t.name}</span>
                    <span className="font-display text-2xl text-white">${t.price}<span className="font-body text-xs text-muted-foreground">/mo</span></span>
                  </div>
                  <p className="mt-1 font-body text-xs text-muted-foreground">{t.blurb}</p>
                  <button
                    data-testid={`upgrade-${t.key}`}
                    onClick={() => upgrade(t.key)}
                    disabled={loading}
                    className={`mt-3 flex w-full items-center justify-center gap-2 px-5 py-2.5 font-body text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${t.best ? "bg-accent text-black" : "border border-accent text-accent"}`}
                  >
                    {loading === t.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                    Choose {t.name}
                  </button>
                </div>
              ))}
            </div>
            <a href="/pricing" className="mt-4 block text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-accent">
              Compare all plans →
            </a>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Secure checkout via Stripe · cancel anytime
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export const openUpgrade = (reason) =>
  window.dispatchEvent(new CustomEvent("open-upgrade", { detail: reason }));

// Renders a real download link for Pro users; for Free users it opens the upgrade modal instead.
export function ProDownloadLink({ href, className, children, reason, ...rest }) {
  const { isPro } = useAuth();
  if (isPro) {
    return <a href={href} className={className} {...rest}>{children}</a>;
  }
  return (
    <button type="button" className={className} onClick={() => openUpgrade(reason)} {...rest}>
      {children}
    </button>
  );
}

